import {
  canonicalReferralCode,
  ERROR_CODES,
  isReferralCodeFormat,
  type ApiErrorBody,
  type PanelGrantCreditsInput,
  type PanelGrantKind,
  type ReferralCodeStatus,
} from '@icebreaker/shared';

/**
 * KREDİ YÜKLE GÖVDESİ (referans kodu Story 4 · P6r). Diyaloğun hâli ile
 * sunucuya giden gövde arasındaki kural TEK YERDE ve saftır, böylece iki
 * ayaklı gövde testleri bileşeni çizmeden de koşar.
 *
 * Kural özeti (sunucu şeması `panelGrantCreditsSchema` ile aynı):
 *  · "Yalnız hediye" seçiliyse `kind` YOK, `giftOnly: true` ve kod zorunlu.
 *  · Kod yalnız hediye eklenecekse gider: adaydan açılışta geçerli kod VE
 *    işaretli kutu, kurum detayından açılışta dolu alan.
 *  · `salesInquiryId` YALNIZ kodla ve YALNIZ adaydan açılışta gider.
 */
export type GrantChoice = PanelGrantKind | 'gift_only';

/** Pencerenin nereden açıldığı ve adaydaki kodun hâli. */
export type GrantGiftSource =
  | {
      readonly from: 'lead';
      readonly salesInquiryId: string;
      readonly code: string | null;
      readonly status: ReferralCodeStatus | null;
    }
  | { readonly from: 'organization' };

export interface GrantFormState {
  readonly choice: GrantChoice;
  readonly purchaseDay: string;
  readonly reference: string;
  readonly reason: string;
  /** Adaydan açılışta "Hediyeyi ekle" kutusu. */
  readonly giftOn: boolean;
  /** Kurum detayından açılışta elle yazılan kod. */
  readonly typedCode: string;
  /** Yükleme anında kod reddedildi (409/400): hediye bu pencerede bir daha önerilmez. */
  readonly giftBlocked: boolean;
}

/** Adaydaki kod hediye verebilir mi (kutu çizilir, "Yalnız hediye" açılır). */
export function leadGiftUsable(source: GrantGiftSource): boolean {
  return source.from === 'lead' && source.code !== null && source.status === 'valid';
}

/** Gövdeye girecek kod; hediye eklenmeyecekse `null`. */
export function giftCodeOf(source: GrantGiftSource, state: GrantFormState): string | null {
  if (state.giftBlocked) return null;
  if (source.from === 'lead') {
    if (!leadGiftUsable(source)) return null;
    return state.giftOn || state.choice === 'gift_only' ? source.code : null;
  }
  return state.typedCode.trim() === '' ? null : canonicalReferralCode(state.typedCode);
}

/**
 * "Yalnız hediye" seçilebilir mi: adayda geçerli kod, kurum detayında biçimi
 * doğru yazılmış kod. Kodun kendisi sunucuda yükleme anında doğrulanır.
 */
export function giftOnlyAllowed(source: GrantGiftSource, state: GrantFormState): boolean {
  if (state.giftBlocked) return false;
  if (source.from === 'lead') return leadGiftUsable(source);
  return isReferralCodeFormat(state.typedCode);
}

/** Kurum detayındaki alanın istemci biçim hatası (boş alan hata değildir). */
export function typedCodeInvalid(source: GrantGiftSource, state: GrantFormState): boolean {
  return (
    source.from === 'organization' &&
    state.typedCode.trim() !== '' &&
    !isReferralCodeFormat(state.typedCode)
  );
}

export function buildGrantBody(
  source: GrantGiftSource,
  state: GrantFormState,
): Omit<PanelGrantCreditsInput, 'idempotencyKey'> {
  const code = giftCodeOf(source, state);
  const text = {
    reference: state.reference.trim() || null,
    reason: state.reason.trim() || null,
  };
  const withCode = code
    ? {
        referralCode: code,
        ...(source.from === 'lead' ? { salesInquiryId: source.salesInquiryId } : {}),
      }
    : {};
  if (state.choice === 'gift_only') return { giftOnly: true, ...text, ...withCode };
  return {
    kind: state.choice,
    ...(state.choice === 'pack_12' ? { purchaseDay: state.purchaseDay } : {}),
    ...text,
    ...withCode,
  };
}

/** Yükleme anındaki kod reddi (409 dörtlüsü + kurum kullanımı + 400 bulunamadı). */
export type GiftRejection =
  | 'not_found'
  | 'inactive'
  | 'not_started'
  | 'expired'
  | 'cap_reached'
  | 'already_used';

const REJECTIONS: Readonly<Record<string, GiftRejection>> = {
  [ERROR_CODES.REFERRAL_CODE_NOT_FOUND]: 'not_found',
  [ERROR_CODES.REFERRAL_CODE_INACTIVE]: 'inactive',
  [ERROR_CODES.REFERRAL_CODE_NOT_STARTED]: 'not_started',
  [ERROR_CODES.REFERRAL_CODE_EXPIRED]: 'expired',
  [ERROR_CODES.REFERRAL_CODE_CAP_REACHED]: 'cap_reached',
  [ERROR_CODES.REFERRAL_CODE_ALREADY_USED]: 'already_used',
};

export function giftRejectionOf(error: ApiErrorBody | null): GiftRejection | null {
  return error ? (REJECTIONS[error.code] ?? null) : null;
}

/**
 * Satış adayı reddi: aday bu kurumun değil (404) ya da kodu gövdedeki kodla
 * eşleşmiyor (400 `salesInquiryId`). İkisi de pencerenin bayat açıldığını söyler.
 */
export function inquiryMismatchOf(error: ApiErrorBody | null): boolean {
  if (!error) return false;
  if (error.code === ERROR_CODES.NOT_FOUND) return true;
  return (error.fieldErrors ?? []).some((item) => item.path === 'salesInquiryId');
}
